import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSetGoalContext } from '../../../../context/useSetGoalContext';
import { StepCard } from '../../../../../PulseCheck.styles';
import { cardAnimationVariants } from '../../../../../../BeFi20/BeFi20.meta';
import { Text } from '../../../../../../CommonComponents/OPL/Text/Text';
import { InputNumberMask } from '../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { Select } from '../../../../../../CommonComponents/OPL/Select/Select/Select';
import { Radio } from '../../../../../../CommonComponents/OPL/Radio/Radio';
import { FormValues, validation } from './LargePurchase.meta';
import { Label } from '../../../../../../CommonComponents/OPL/Label/Label';
import { AT_RETIREMENT } from '../../../../utils/getDisplayDate';
import { FORM_ID } from '../../../../SetGoal.meta';
import { StepWrapper } from '../../../../../components/StepWrapper/StepWrapper';
import { selectCurrentClient } from '../../../../../../CommonSelectors/CommonAreaSelector';

export const LargePurchase = () => {
  const { wizard, setWizard, onSuccess, options } = useSetGoalContext();

  const { Dob: primaryDob } = useSelector(selectCurrentClient);

  const primaryYob = primaryDob ? new Date(primaryDob).getFullYear() : null;

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    setValue,
  } = useForm<FormValues>({
    defaultValues: { inflation: options.Inflation, ...wizard.purchase },
    resolver: yupResolver(validation),
  });

  const onSubmit = handleSubmit(values => {
    const newWizard = { ...wizard, purchase: values };

    setWizard(newWizard);

    onSuccess(newWizard);
  });

  const isRecurringPurchase = watch('isRecurringPurchase');

  return (
    <StepWrapper as="form" id={FORM_ID} onSubmit={onSubmit}>
      <StepCard variants={cardAnimationVariants} initial="enter" animate="visible">
        <Text as="h1" fontSize="h4" fontWeight={600} mb="16">
          How much do you want to spend?
        </Text>

        <Controller
          control={control}
          name="amount"
          render={({ field: { onChange, ...field } }) => (
            <InputNumberMask
              label="Amount"
              decimalScale={2}
              allowNegative={false}
              leftIcon="far fa-dollar-sign"
              formVariant={errors.amount && 'error'}
              helperText={errors.amount?.message}
              onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
              mb="16"
              css="width: 300px"
              {...field}
            />
          )}
        />

        <Controller
          control={control}
          name="inflation"
          render={({ field: { onChange, ...field } }) => (
            <InputNumberMask
              label="Inflation"
              rightIcon="far fa-percent"
              decimalScale={2}
              allowNegative={false}
              onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
              formVariant={errors.inflation && 'error'}
              helperText={errors.inflation?.message}
              css="width: 300px"
              {...field}
            />
          )}
        />

        <Text variant="formLabel" mt="16" mb="4">
          Frequency
        </Text>

        <Controller
          control={control}
          name="isRecurringPurchase"
          render={({ field: { value, name, onChange } }) => {
            return (
              <Box display="flex" flexDirection="column" mb="8">
                <Radio name={name} checked={!value} onChange={() => onChange(false)}>
                  One-Time
                </Radio>

                <Radio name={name} checked={value} onChange={() => onChange(true)} mt="8">
                  Recurring
                </Radio>
              </Box>
            );
          }}
        />

        {isRecurringPurchase ? (
          <Box display="flex" alignItems="flex-start" css="gap: 16px">
            <Controller
              name="recurringOptions.startYear"
              control={control}
              render={({ field }) => (
                <Select
                  innerRef={field.ref}
                  label="Starting"
                  value={
                    typeof field.value === 'number'
                      ? ({
                          value: field.value,
                          label: field.value === AT_RETIREMENT ? 'At Retirement' : field.value,
                        } as any)
                      : null
                  }
                  options={[
                    { label: 'At Retirement', value: AT_RETIREMENT },
                    ...options.YearsOptions.map(year => ({
                      label: year,
                      value: year,
                      rightLabel: primaryYob ? `Age ${year - primaryYob}` : undefined,
                    })),
                  ]}
                  onChange={option => {
                    field.onChange(option?.value);

                    setValue('recurringOptions.isAtRetirement', option?.value === AT_RETIREMENT);
                  }}
                  formVariant={errors.recurringOptions?.startYear && 'error'}
                  helperText={errors.recurringOptions?.startYear?.message}
                  css="flex: 1"
                />
              )}
            />

            <Controller
              control={control}
              name="recurringOptions.repeatEvery"
              render={({ field: { onChange, ...field } }) => (
                <InputNumberMask
                  label="Every"
                  decimalScale={2}
                  allowNegative={false}
                  onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                  formVariant={errors.recurringOptions?.repeatEvery && 'error'}
                  helperText={errors.recurringOptions?.repeatEvery?.message}
                  style={{ width: 80 }}
                  {...field}
                />
              )}
            />
            <Label mt={34}>year(s)</Label>
            <Controller
              control={control}
              name="recurringOptions.repeatFor"
              render={({ field: { onChange, ...field } }) => (
                <InputNumberMask
                  label="For"
                  decimalScale={2}
                  allowNegative={false}
                  onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                  formVariant={errors.recurringOptions?.repeatFor && 'error'}
                  helperText={errors.recurringOptions?.repeatFor?.message}
                  style={{ width: 80 }}
                  {...field}
                />
              )}
            />
            <Label mt={34}>year(s)</Label>
          </Box>
        ) : (
          <Controller
            name="oneTimeOptions.targetYear"
            control={control}
            render={({ field }) => (
              <Select
                innerRef={field.ref}
                label="Desired Purchase Year"
                value={
                  typeof field.value === 'number'
                    ? ({
                        value: field.value,
                        label: field.value === AT_RETIREMENT ? 'At Retirement' : field.value,
                      } as any)
                    : null
                }
                options={[
                  { label: 'At Retirement', value: AT_RETIREMENT },
                  ...options.YearsOptions.map(year => ({
                    label: year,
                    value: year,
                    rightLabel: primaryYob ? `Age ${year - primaryYob}` : undefined,
                  })),
                ]}
                onChange={option => {
                  field.onChange(option?.value);

                  setValue('oneTimeOptions.isAtRetirement', option?.value === AT_RETIREMENT);
                }}
                formVariant={errors.oneTimeOptions?.targetYear && 'error'}
                helperText={errors.oneTimeOptions?.targetYear?.message}
                css="width: 300px"
              />
            )}
          />
        )}
      </StepCard>
    </StepWrapper>
  );
};
